// Drobné pomocníky pro tvorbu DOM bez knihoven.

type Child = Node | string | number | null | undefined | false | Child[];

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs?: Record<string, unknown> | null,
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      if (v === null || v === undefined || v === false) continue;
      if (k === 'class') el.className = String(v);
      else if (k === 'style' && typeof v === 'object') {
        for (const [sk, sv] of Object.entries(v as Record<string, string>)) {
          if (sk.startsWith('--')) el.style.setProperty(sk, sv); // CSS proměnné nejdou přes Object.assign
          else (el.style as unknown as Record<string, string>)[sk] = sv;
        }
      }
      else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
      else if (k === 'dataset' && typeof v === 'object') Object.assign(el.dataset, v as Record<string, string>);
      else if (v === true) el.setAttribute(k, '');
      else el.setAttribute(k, String(v));
    }
  }
  append(el, children);
  return el;
}

export function append(el: Node, children: Child[]): void {
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    if (Array.isArray(c)) append(el, c);
    else if (c instanceof Node) el.appendChild(c);
    else el.appendChild(document.createTextNode(String(c)));
  }
}

export function clear(el: Element): void {
  while (el.firstChild) el.removeChild(el.firstChild);
}

export function svg(markup: string, cls = ''): SVGElement {
  const tpl = document.createElement('template');
  tpl.innerHTML = markup.trim();
  const el = tpl.content.firstElementChild as SVGElement;
  if (cls) el.setAttribute('class', cls);
  return el;
}

export function debounce<A extends unknown[]>(fn: (...a: A) => void, ms: number): ((...a: A) => void) & { cancel(): void; flush(): void } {
  let timer: number | undefined;
  let lastArgs: A | null = null;
  const wrapped = ((...a: A) => {
    lastArgs = a;
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      timer = undefined;
      const args = lastArgs;
      lastArgs = null;
      if (args) fn(...args);
    }, ms);
  }) as ((...a: A) => void) & { cancel(): void; flush(): void };
  wrapped.cancel = () => {
    window.clearTimeout(timer);
    timer = undefined;
    lastArgs = null;
  };
  wrapped.flush = () => {
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timer = undefined;
      const args = lastArgs;
      lastArgs = null;
      if (args) fn(...args);
    }
  };
  return wrapped;
}

export function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

export function uid(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

/** Vrátí true, když je cíl události textové pole (zkratky se pak nepřebírají). */
export function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

/** Zkratka z klávesové události ve tvaru "Ctrl+Shift+K" (stejný zápis jako Command::shortcut). */
export function shortcutOf(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.ctrlKey || e.metaKey) parts.push('Ctrl');
  if (e.altKey) parts.push('Alt');
  if (e.shiftKey) parts.push('Shift');
  let key = e.key;
  if (key === ' ') key = 'Space';
  else if (key.length === 1) key = key.toUpperCase();
  else if (key === 'Escape') key = 'Esc';
  parts.push(key);
  return parts.join('+');
}

export function onOutsideClick(el: HTMLElement, handler: () => void, anchor?: HTMLElement | null): () => void {
  const listener = (e: PointerEvent) => {
    const target = e.target as Node;
    if (el.contains(target)) return;
    if (anchor?.contains(target)) swallowNextClick(anchor); // druhý klik na tlačítko jen zavře, neotevře znovu
    handler();
  };
  window.setTimeout(() => document.addEventListener('pointerdown', listener, true), 0);
  return () => document.removeEventListener('pointerdown', listener, true);
}

/** Následující klik na prvek se zahodí (nabídka se zavřela už při stisku, klik by ji otevřel znovu). */
export function swallowNextClick(el: HTMLElement): void {
  const block = (e: Event) => { e.stopImmediatePropagation(); e.preventDefault(); };
  el.addEventListener('click', block, { capture: true, once: true });
  window.setTimeout(() => el.removeEventListener('click', block, true), 400);
}

/** Prázdné PHP pole přijde v JSON jako [] – parametry oken musí být vždy objekt. */
export function asObject(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);
}
